/* eslint-disable @typescript-eslint/no-unused-vars */

import i18n from 'i18next';
import { useTPAxios } from '@/api/useTPAxios.ts';
import { APIResponse, RequestProps } from '@/utils/response.utils';
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { AxiosInstance, AxiosResponse } from 'axios';
import { ApiError } from '@/utils/apiError.model';
import { AuthStore } from './auth.store';
import { User } from './user.model';
import { SettingsStore } from '../settings/settings.store';
import { Lang } from '../wallet/product.model';

export type SignInApiResponse = {
  data: {
    user: User;
    account: User;
    token: string;
    refreshToken: string;
  }
};

/* Verify Account */
const verifyAccount = async (
  email: string,
  TPAxios: AxiosInstance
): Promise<APIResponse> => {
  const body = {
    email,
  };

  const response = await TPAxios.post<APIResponse>(
    '/auth/verify',
    body,
  );

  return response.data;
}

export const useVerifyAccount = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (email: string) => verifyAccount(email, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: unknown) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

/* Verify Signin */
const signin = async (
  email: string,
  pin: string,
  TPAxios: AxiosInstance
): Promise<SignInApiResponse> => {
  const body = {
    email,
    pin: parseInt(pin),
  };

  const response = await TPAxios.post<SignInApiResponse>(
    '/auth/signin',
    body,
  );

  return response.data;
}

export const useSignin = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();
  const { signIn } = AuthStore();

  return useMutation({
    mutationFn: ({ email, pin }: { email: string, pin: string }) => signin(email, pin, TPAxios),
    onSuccess: async (data) => {
      const { account, token: accessToken, refreshToken } = data.data;
      await signIn({ data: account, accessToken, refreshToken });

      if (account.language) {
        const lang: Lang = (account.language === 'EN' || account.language === 'FR' || account.language === 'JA')
          ? account.language
          : 'EN';
        SettingsStore.getState().setLanguage(lang);
      }
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

const signInWithHash = async (
  email: string,
  iv: string,
  TPAxios: AxiosInstance,
  id?: string,
): Promise<SignInApiResponse> => {
  const body = {
    email,
    ...(id && { id }),
    iv,
  };

  const response = await TPAxios.post<SignInApiResponse>(
    '/auth/signin/hash',
    body,
  );

  return response.data;
}

export const useSignInWithHash = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();
  const { signIn } = AuthStore();

  return useMutation({
    mutationFn: ({ email, id, iv }: { email: string, id?: string, iv: string }) => signInWithHash(email, iv, TPAxios, id),
    onSuccess: async (data) => {
      const { account, token: accessToken, refreshToken } = data.data;

      if (account.language) {
        const lang: Lang = (account.language === 'EN' || account.language === 'FR' || account.language === 'JA')
          ? account.language
          : 'EN';
        SettingsStore.getState().setLanguage(lang);
      }

      await signIn({ data: account, accessToken, refreshToken });
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

export type SignupProps = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  phoneCode?: string;
  language?: string;
}

/* Verify Signup */
const signup = async (
  body: SignupProps,
  TPAxios: AxiosInstance,
): Promise<SignInApiResponse> => {
  const data = Object.fromEntries(
    Object.entries(body).filter(
      ([_, v]) => v !== null && v !== undefined && v !== ''
    )
  );
  const response = await TPAxios.post<SignInApiResponse>(
    '/auth/signup',
    data,
  );

  return response.data;
}

export const useSignup = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (body: SignupProps) => signup(body, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

/* Resend Pin */
const resendPin = async (
  email: string,
  TPAxios: AxiosInstance
): Promise<APIResponse> => {
  const body = {
    email,
  };

  const response = await TPAxios.post<APIResponse>(
    '/auth/pin/resend',
    body,
  );

  return response.data;
}

export const useResendPin = (
  props: RequestProps
) => {
  const { onSuccess, onError } = props;
  const TPAxios = useTPAxios();

  return useMutation({
    mutationFn: (email: string) => resendPin(email, TPAxios),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      const err = error as ApiError;
      const errorCode = err.response?.data.errorCode;
      onError(i18n.t(`errors.${errorCode}`));
    }
  })
}

// Load customer profile
const loadProfile = async (
  TPAxios: AxiosInstance
): Promise<User> => {
  const response = await TPAxios.get<AxiosResponse<User>>(
    '/customers/me',
  );

  return response.data.data;
}

export const useLoadProfile = () => {
  const TPAxios = useTPAxios();

  const options = queryOptions({
    queryKey: ['me'],
    queryFn: async () => await loadProfile(TPAxios),
  });

  return useSuspenseQuery(options);
}
